// src/components/NewLeadsLayer.jsx
// "New leads to triage" — stage 1 of Today.
//
// WHY THIS EXISTS
// Lead triage lived in MorningPlanningTool step 1, and
// `/api/leads/list?status=Identified` had exactly ONE caller in the whole
// codebase: that step. TodayPage had zero references to leads. So retiring
// Morning — which the IA plan does, because three of its four steps are stubs
// or duplicates — would have silently removed the app's only lead-triage entry
// point. This is the destination that had to exist first.
//
// WHAT CHANGED IN THE MOVE
// Morning's "Mark Reviewed ✓" wrote to sessionStorage and nothing else. The
// lead's status stayed 'Identified' server-side, so the same leads came back
// on the next visit and a closed tab lost the work. That is not a behaviour
// worth porting. This archives through /api/leads/archive and only removes
// the row once the server agrees — the same rule applied to the queue handlers
// in VictorPage (IA-3).
//
// It fetches independently rather than extending /api/today/brief: the brief is
// a single generated payload and adding a live, mutable list to it would couple
// triage state to a cached AI response.

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './NewLeadsLayer.css';

const PRIORITY_STYLES = {
  High:   { bg: 'var(--dm-red-light)',    color: 'var(--dm-red)' },
  Medium: { bg: '#fff7ed',                color: '#c2410c' },
  Low:    { bg: 'var(--dm-grey-100)',     color: '#475569' },
};

export default function NewLeadsLayer({ onNavigate }) {
  const [leads,   setLeads]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [busyId,  setBusyId]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await apiFetch('/api/leads/list?status=Identified&page_size=100');
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.error || 'Could not load leads');
      setLeads(data.leads || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Dismiss a lead from triage. Uses /api/leads/archive, which sets
  // status='Archived' — the vocabulary the rest of the app actually uses, and
  // the same call LeadsPage's archive button makes (LeadsPage.jsx:473).
  //
  // A first draft posted status:'Qualified' to leads/update/<id>. There is no
  // DB constraint on the column, so it would have been accepted — and the lead
  // would then have matched no filter in any view, disappearing from the app
  // rather than moving through it. The status vocabulary is Identified /
  // Archived / Active; 'Qualified' was invented.
  //
  // Unlike Morning's "Mark Reviewed ✓", which wrote only to sessionStorage and
  // left the lead 'Identified' forever, this persists — and the row only leaves
  // the list once the server agrees.
  const archiveLead = async (lead) => {
    setBusyId(lead.id);
    setError(null);
    try {
      const res = await apiFetch('/api/leads/archive', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ leadId: lead.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || `archive failed (${res.status})`);
      setLeads(prev => prev.filter(l => l.id !== lead.id));
    } catch (e) {
      setError(`Could not archive that lead — it is still in the list. (${e.message})`);
    } finally {
      setBusyId(null);
    }
  };

  // Nothing to triage is the good state, and Today's other layers hide
  // themselves when empty rather than showing a placeholder. Match that.
  if (!loading && !error && leads.length === 0) return null;

  return (
    <div className="td-layer">
      <div className="td-layer-header">
        <span className="td-layer-icon" style={{ color: 'var(--dm-indigo)' }}>🎯</span>
        <span className="td-layer-title">New leads to triage</span>
        {leads.length > 0 && <span className="td-layer-count">{leads.length}</span>}
      </div>

      {error && (
        <div className="nl-error" role="alert">
          {error}
          <button onClick={load}>Retry</button>
        </div>
      )}

      <div className="td-items">
        {loading ? (
          <div className="nl-loading">Loading new leads…</div>
        ) : (
          leads.map(lead => {
            // computedPriority is the scorer's answer (IA-7b); `priority` is
            // the column a human may have set. Prefer the computed rank, fall
            // back to the stored one. The list arrives already sorted by score.
            const rank = lead.computedPriority || lead.priority;
            const pri  = PRIORITY_STYLES[rank] || PRIORITY_STYLES.Medium;
            return (
              <div key={lead.id} className="nl-item">
                <div className="nl-item-main">
                  <div className="nl-item-title">
                    <span className="nl-title-text">{lead.title || 'Untitled lead'}</span>
                    <span className="nl-priority" style={{ background: pri.bg, color: pri.color }}>
                      {rank || 'Medium'}
                    </span>
                  </div>
                  {/* Field names come from mapLead in api/leads/list.js:4, not
                      from the leads table: the API renames opportunity_hypothesis
                      -> opportunityHypothesis and exposes accountId (no name).
                      A first draft read lead.hypothesis and lead.accountName —
                      both always undefined, so these two lines would simply
                      never have rendered and nothing would have said why. */}
                  {lead.opportunityHypothesis && (
                    <div className="nl-item-hypothesis">{lead.opportunityHypothesis}</div>
                  )}
                  {lead.dealSize && (
                    <div className="nl-item-account">Deal size: {lead.dealSize}</div>
                  )}
                </div>
                <div className="nl-item-actions">
                  <button className="nl-btn nl-btn--primary"
                          onClick={() => onNavigate && onNavigate('leads')}>
                    Work it →
                  </button>
                  <button className="nl-btn"
                          disabled={busyId === lead.id}
                          onClick={() => archiveLead(lead)}>
                    {busyId === lead.id ? '…' : 'Dismiss'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
